<script setup lang="ts">
import { PromiseController } from '@repo/alt/common/Utils/Async.js'
import type { ShowcaseContentResourceDto } from '@repo/api/web/models'
import type { LightGallery } from 'lightgallery/lightgallery'
import 'lightgallery/css/lightgallery.css'

import UiPicture from '~/app/components/Image/UiPicture.vue'

/**
 * Material gallery (CMS MR 12): images from ContentItemResource.
 * Order comes from the backend. Click opens LightGallery (same lazy pattern as PDP).
 */
const LightgalleryContainer = defineAsyncComponent(
  () => import('lightgallery/vue'),
)

const props = defineProps<{
  items: ShowcaseContentResourceDto[]
}>()

const { support } = useResources()
const initGalleryPromise = new PromiseController<LightGallery>()
const $gallery = ref<LightGallery>()
const needShowGallery = ref(false)

const slides = computed(() =>
  props.items.map(image => ({
    id: image.resourceId,
    src: support.getResourceUrl(image.resourceId),
    alt: image.displayName || '',
  })),
)

async function openGallery(index: number) {
  needShowGallery.value = true

  if (!$gallery.value) {
    await initGalleryPromise.promise
  }

  $gallery.value?.openGallery(index)
}

function handleInitGallery(gallery: { instance: LightGallery }) {
  $gallery.value = gallery.instance
  initGalleryPromise.resolve($gallery.value)
}
</script>

<template>
  <section
    v-if="props.items.length"
    class="mt-10"
  >
    <h2 class="mb-4 text-xl font-semibold">
      Галерея
    </h2>

    <ClientOnly>
      <template #fallback>
        <div class="hidden" />
      </template>
      <LightgalleryContainer
        v-if="needShowGallery"
        key="content-gallery"
        :onInit="handleInitGallery"
      >
        <a
          v-for="slide in slides"
          :key="slide.id"
          :data-src="slide.src"
        />
      </LightgalleryContainer>
    </ClientOnly>

    <div class="grid grid-cols-2 gap-3 md:grid-cols-3">
      <button
        v-for="(slide, index) in slides"
        :key="slide.id"
        type="button"
        class="aspect-square w-full cursor-pointer overflow-hidden rounded-xl p-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        :aria-label="slide.alt || `Открыть изображение ${index + 1}`"
        @click="openGallery(index)"
      >
        <UiPicture
          :src="slide.id"
          variant="imageFull"
          class="pointer-events-none size-full"
          sizes="50vw md:240px"
          loading="lazy"
          :alt="slide.alt"
        />
      </button>
    </div>
  </section>
</template>
